import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const ProjectModal = ({ project, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Reset index when project changes
    setCurrentImageIndex(0);
  }, [project]);

  if (!project) return null;

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % project.images.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + project.images.length) % project.images.length);
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
  };

  const imageVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose} // Close when clicking outside the modal content
        >
          <motion.div
            className="bg-white rounded-lg shadow-2xl max-w-5xl w-full h-[80vh] max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal content
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 z-20"
            >
              <X size={24} />
            </button>

            {/* Image Carousel - Left Side */}
            <div className="w-full md:w-1/2 p-8 flex items-center justify-center">
              <div className="relative w-full h-full rounded-lg shadow-lg overflow-hidden">
                <AnimatePresence initial={false} custom={currentImageIndex}>
                  <motion.img
                    key={currentImageIndex}
                    src={project.images[currentImageIndex]}
                    alt={`Project Image ${currentImageIndex + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    custom={currentImageIndex}
                    variants={imageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: "spring", stiffness: 300, damping: 30 },
                      opacity: { duration: 0.2 }
                    }}
                  />
                </AnimatePresence>
                {project.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors duration-200 z-10"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors duration-200 z-10"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Project Details - Right Side */}
            <div className="w-full md:w-1/2 p-8 overflow-y-auto">
               {/* <h3 className="text-2xl font-bold text-gray-800 mb-4">{project.modalTitle}</h3>*/}
              {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-gray-700">
                <p><span className="font-semibold">Año:</span> {project.year}</p>
                <p><span className="font-semibold">Ciudad:</span> {project.city}</p>
              </div> */}
              {/* <p className="text-gray-600 leading-relaxed mb-6">{project.description}</p> */}
              {project.detailed_sections && project.detailed_sections.length > 0 && (
                <div className="mt-6 space-y-4">
                  {project.detailed_sections.map((section, index) => (
                    <div key={index}>
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">{section.subtitle}</h4>
                      <ul className="list-disc list-inside text-gray-600 space-y-1 pl-2">
                        {section.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              <br></br>
              {project.details2 && (
                <div className="mt-4">
                  {/*<h4 className="text-xl font-semibold text-gray-800 mb-2">Detalles Adicionales:</h4>*/}
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {project.details2.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;